/**
 * AdditionalPartiesSection — optional work-order party roles in the create
 * wizard (#1108, completes #768).
 *
 * Shows only the roles that have actually been picked; a "+" menu offers the
 * remaining optional roles (orderer and primary_contact are handled
 * elsewhere in CustomerVehicleStep and are not offered here). Selections are
 * held in local wizard state and only persisted via
 * PUT /work-orders/:id/parties/:role after the work order is created.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, IconButton, Menu, MenuItem, Paper, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { type PartyRef } from '@/components/parties/PartyPicker';
import { WORK_ORDER_PARTY_ROLES, type WorkOrderPartyRole } from '@/lib/workOrderPartyRoles';
import { AddWorkOrderPartyDialog } from './AddWorkOrderPartyDialog';

const ADDITIONAL_PARTY_ROLES: readonly WorkOrderPartyRole[] = WORK_ORDER_PARTY_ROLES.filter(
  (role) => role !== 'orderer' && role !== 'primary_contact',
);

interface AdditionalPartiesSectionProps {
  parties: Partial<Record<WorkOrderPartyRole, PartyRef>>;
  onAddParty: (role: WorkOrderPartyRole, party: PartyRef) => void;
  onRemoveParty: (role: WorkOrderPartyRole) => void;
  setIsDirty: (v: boolean) => void;
}

export function AdditionalPartiesSection({
  parties,
  onAddParty,
  onRemoveParty,
  setIsDirty,
}: AdditionalPartiesSectionProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [dialogRole, setDialogRole] = useState<WorkOrderPartyRole | null>(null);

  const addedRoles = ADDITIONAL_PARTY_ROLES.filter((role) => parties[role]);
  const availableRoles = ADDITIONAL_PARTY_ROLES.filter((role) => !parties[role]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" color="text.secondary">
          {t('create.additionalParties')}
        </Typography>
        {availableRoles.length > 0 && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            {t('create.addParty')}
          </Button>
        )}
      </Stack>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {availableRoles.map((role) => (
          <MenuItem
            key={role}
            onClick={() => {
              setMenuAnchor(null);
              setDialogRole(role);
            }}
          >
            {t(`parties.roles.${role}`)}
          </MenuItem>
        ))}
      </Menu>

      {addedRoles.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {addedRoles.map((role) => {
            const party = parties[role]!;
            return (
              <Paper
                key={role}
                variant="outlined"
                sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={t(`parties.roles.${role}`)} size="small" variant="outlined" />
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {party.name}
                    {party.phone ? ` · ${party.phone}` : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={tc('edit')}>
                    <IconButton size="small" onClick={() => setDialogRole(role)} aria-label={tc('edit')}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tc('delete')}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        onRemoveParty(role);
                        setIsDirty(true);
                      }}
                      aria-label={tc('delete')}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {dialogRole && (
        <AddWorkOrderPartyDialog
          open={dialogRole !== null}
          role={dialogRole}
          onClose={() => setDialogRole(null)}
          onAdd={(role, party) => {
            onAddParty(role, party);
            setIsDirty(true);
          }}
        />
      )}
    </Box>
  );
}
